import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, MultiChoice, NumberInputWithOptions} from 'platform/components';
import {Grid, Show} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNotEmpty, isNotNil} from 'ramda';

import {
  selectTenant,
  useGetAuctionServerListQuery,
  useSourcingGetSourcingServersQuery,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {ALLOWED_SELLER_TYPE_KEYS} from '../../../../../../constants/allowedSellerTypeKeys';
import {sourcingFilterConstants} from '../../../../../../constants/sourcingFilterConstants';
import {UNSUPPORTED_ORIGIN_COUNTRY_CODES} from '../../../../../../constants/unsupportedOriginCountryCodes';
import {useApiEnums} from '../../../../../../hooks/useApiEnums';
import {SourcingType, useSourcingType} from '../../../../../../hooks/useSourcingType';
import {AuctionTypeFilter} from '../../../../../../types/AuctionTypeFilter';
import {getAuctionTypePresentation} from '../../../../../../utils/getAuctionTypePresentation';
import {CarStatusFilter} from '../../../../types/CarStatusFilter';
import {FilterField} from '../../../FilterEditor/components/FilterField';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {allowedCountryCodes} from '../../constants/AllowedCountryCodes';
import {SourcingSlider} from '../SourcingSlider/SourcingSlider';
import {FilterLocation} from './components/FilterLocation';

const carStatusOptions = [
  {value: CarStatusFilter.ACTIVE, label: i18n.t('entity.auction.labels.active')},
  {value: CarStatusFilter.ENDED, label: i18n.t('entity.auction.labels.ended')},
];

export function FilterSeller(props: TestIdProps & {isCompact?: boolean}) {
  const auctionTypeOptions = Object.values(AuctionTypeFilter).map((value) => ({
    value,
    label: getAuctionTypePresentation(value)?.label ?? value,
  }));
  const sourcingType = useSourcingType();
  const {filters, onUpdateFilters} = useFilters();
  const {getEnum, isLoading} = useApiEnums();
  const {data: {country: countryCode} = {}} = useSelector(selectTenant);
  const {data: sellerServers} = useSourcingGetSourcingServersQuery(
    isNotNil(countryCode) ? {countryCode} : skipToken
  );
  const sellerServerOptions =
    sellerServers?.map((sellerServer) => ({
      value: sellerServer.code,
      label: sellerServer.name,
    })) ?? [];

  const {data: auctionServers} = useGetAuctionServerListQuery(undefined, {
    skip: sourcingType !== SourcingType.AUCTIONS,
  });
  const auctionServerOptions =
    auctionServers
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((auctionServer) => ({
        value: auctionServer.id,
        label: auctionServer.name,
      })) ?? [];

  const countries = getEnum('country')
    .filter((country) => allowedCountryCodes.includes(country.key))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Wider than the seller countries (imports from the US, Korea…). Countries Alpha does not know
  // are left out, they would fail the whole classifieds request.
  const originCountries = getEnum('country')
    .filter((country) => !UNSUPPORTED_ORIGIN_COUNTRY_CODES.includes(country.key))
    .sort((a, b) => a.label.localeCompare(b.label));

  const sellerTypes = getEnum('seller_type').filter((sellerType) =>
    ALLOWED_SELLER_TYPE_KEYS.includes(sellerType.key)
  );

  return (
    <DataStatus isLoading={isLoading}>
      <Grid
        columns={props.isCompact ? 1 : [1, 2, 4]}
        spacing={8}
        data-testid="block-filters-seller"
      >
        <FilterField name="sellerCountry">
          <MultiChoice
            noOptionsMessage={() => i18n.t('general.labels.noOptions')}
            menuInPortal={props.isCompact}
            label={getSellerCountryLabel()}
            placeholder={i18n.t('general.labels.chooseOptions')}
            value={filters.sellerCountry ?? null}
            options={countries}
            onChange={(value) => onUpdateFilters('sellerCountry', value ?? undefined)}
            data-testid={suffixTestId('sellerCountry', props)}
          />
        </FilterField>

        <Show
          when={sourcingType === SourcingType.CLASSIFIEDS}
          whenFeatureEnabled={featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER}
        >
          <FilterField name="originCountry">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('entity.vehicle.labels.countryOfOrigin')}
              tooltip={i18n.t('entity.sourcing.filters.labels.originCountryTooltip')}
              placeholder={i18n.t('general.labels.chooseOptions')}
              value={filters.originCountry ?? null}
              options={originCountries}
              onChange={(value) =>
                onUpdateFilters(
                  'originCountry',
                  isNotNil(value) && isNotEmpty(value) ? value : undefined
                )
              }
              data-testid={suffixTestId('originCountry', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <FilterField name="sellerServer">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('page.filters.labels.sellerServer')}
              placeholder={i18n.t('general.labels.chooseOption')}
              value={filters.sellerServer ?? null}
              options={sellerServerOptions}
              onChange={(value) => onUpdateFilters('sellerServer', value ?? undefined)}
              data-testid={suffixTestId('sellerServer', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <FilterField name="sellerType">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('page.filters.labels.sellerType')}
              placeholder={i18n.t('general.labels.chooseOption')}
              options={sellerTypes}
              value={filters.sellerType ?? null}
              onChange={(value) => onUpdateFilters('sellerType', value ?? undefined)}
              data-testid={suffixTestId('sellerType', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.AUCTIONS}>
          <FilterField name="auctionType">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('page.filters.labels.auctionType')}
              placeholder={i18n.t('general.labels.chooseOption')}
              options={auctionTypeOptions}
              value={filters.auctionType ?? null}
              onChange={(value) => onUpdateFilters('auctionType', value ?? undefined)}
              data-testid={suffixTestId('auctionType', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.AUCTIONS}>
          <FilterField name="carStatus">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('page.filters.labels.carStatus')}
              placeholder={i18n.t('general.labels.chooseOption')}
              options={carStatusOptions}
              value={filters.carStatus ?? null}
              onChange={(value) => onUpdateFilters('carStatus', value ?? undefined)}
              data-testid={suffixTestId('carStatus', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.AUCTIONS}>
          <FilterField name="auctionServer">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('page.filters.labels.auctionServer')}
              placeholder={i18n.t('general.labels.chooseOption')}
              options={auctionServerOptions}
              value={filters.auctionServer ?? null}
              onChange={(value) => onUpdateFilters('auctionServer', value ?? undefined)}
              data-testid={suffixTestId('auctionServer', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <FilterField name="avgNumberAds">
            <SourcingSlider
              label={i18n.t('entity.warehouse.labels.averageCarsInStock')}
              min={sourcingFilterConstants.AVG_NUMBER_ADS_MIN}
              max={sourcingFilterConstants.AVG_NUMBER_ADS_MAX}
              marks={sourcingFilterConstants.AVG_NUMBER_ADS_MARKS}
              hasMaxPlus
              shouldHideSlider
              value={filters.avgNumberAds}
              onChange={(value) => onUpdateFilters('avgNumberAds', value)}
              data-testid={suffixTestId('avgNumberAds', props)}
            />
          </FilterField>
        </Show>

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <FilterField name="address">
            <FilterLocation
              key={
                isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN)
                  ? JSON.stringify(filters.address)
                  : undefined
              }
            />
          </FilterField>

          <FilterField name="radius">
            <NumberInputWithOptions
              label={`${i18n.t('page.filters.labels.radius')} (km)`}
              placeholder={i18n.t('general.labels.chooseOption')}
              options={[5, 10, 20, 50, 100]}
              decimalPlaces={0}
              value={filters.radius ?? null}
              // min prop is not implemented, so here is a workaround
              onChange={(value) => onUpdateFilters('radius', (value ?? 0) < 0 ? 0 : value)}
              data-testid={suffixTestId('radius', props)}
              isClearable
            />
          </FilterField>
        </Show>
      </Grid>
    </DataStatus>
  );
}

function getSellerCountryLabel() {
  return i18n.t(
    isFeatureEnabled(featureFlags.SOURCING_FILTER_REDESIGN)
      ? 'entity.vehicle.labels.sellerCountry'
      : 'page.filters.labels.sellerCountry'
  );
}
